import { useCallback, useMemo, useState } from 'react'
// @mui
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import FormProvider, { RHFTextField } from 'src/components/hook-form'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  Slide,
  Fade,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { IEventItem } from 'src/types/events'
import { yupResolver } from '@hookform/resolvers/yup'
import { EventStepOneSchema } from 'src/validators/event.schema'
import { Controller, useForm } from 'react-hook-form'
import Iconify from 'src/components/iconify'
import { useSnackbar } from 'src/components/snackbar'
import { useParams } from 'src/routes/hook'
import axiosInstance from 'src/utils/axios'
import { masks } from 'src/utils/masks'

// ----------------------------------------------------------------------

interface EventStepperOneProps {
  activeStep: number
  methods?: any
  handleBack: () => void
  handleNext: (data: any) => void
  handleSave: () => Promise<void>
}

interface IEventStepOne {
  name: string
  date: Date
  tax: {
    credit: number
    debit: number
    money: number
    pix: number
  }
  installmentConfig: {
    enabled: boolean
    maxInstallments: number
    minAmountForInstallment: number
  }
  superPassword: string
  extraCost: number
}

export default function EventStepperOne({
  activeStep,
  handleBack,
  handleNext,
  methods,
  handleSave,
}: EventStepperOneProps) {
  const { handleSubmit, control, watch, getValues } = methods
  const { enqueueSnackbar } = useSnackbar()
  const params = useParams()
  const [isLoadingInstallment, setIsLoadingInstallment] = useState(false)

  const eventId = params.id

  const handleMaxInstallmentsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target

      // Permite limpar o campo
      if (value === '') {
        methods.setValue('installmentConfig.maxInstallments', '')
        return
      }

      // Remove qualquer caracter que não seja número
      const numericValue = parseInt(value.replace(/\D/g, ''), 10)

      if (Number.isNaN(numericValue)) {
        return // Ignora se não for um número válido
      }

      // Garante que o valor está entre 1 e 24
      const clampedValue = Math.max(1, Math.min(numericValue, 24))

      methods.setValue('installmentConfig.maxInstallments', clampedValue)
    },
    [methods],
  )

  // Helper function to convert currency values to cents
  const convertCurrencyToCents = useCallback((value: string | number) => {
    if (!value) return 0
    const stringValue = String(value).replace(/[^0-9,]/g, '') // Keep only numbers and comma
    if (stringValue.includes(',')) {
      const parts = stringValue.split(',')
      const reais = parseInt(masks.number(parts[0]), 10) || 0
      const centavos = parseInt((parts[1] || '0').padEnd(2, '0').substring(0, 2), 10)
      return reais * 100 + centavos
    }
    return parseInt(masks.number(stringValue), 10) * 100 || 0
  }, [])

  // Save installment configuration
  const handleSaveInstallmentConfig = useCallback(async () => {
    if (!eventId) {
      enqueueSnackbar('Evento deve ser criado primeiro antes de salvar as configurações', {
        variant: 'warning',
      })
      return
    }

    try {
      setIsLoadingInstallment(true)
      const currentData = getValues()

      const processedData = {
        installmentConfig: {
          ...currentData.installmentConfig,
          minAmountForInstallment: convertCurrencyToCents(
            currentData.installmentConfig.minAmountForInstallment,
          ),
        },
      }

      await axiosInstance.put(`/event/${eventId}`, processedData)
      enqueueSnackbar('Configurações de parcelamento salvas com sucesso!', {
        variant: 'success',
      })
    } catch (error) {
      console.error('Erro ao salvar configurações:', error)
      enqueueSnackbar('Erro ao salvar configurações de parcelamento', {
        variant: 'error',
      })
    } finally {
      setIsLoadingInstallment(false)
    }
  }, [eventId, enqueueSnackbar, getValues, convertCurrencyToCents])

  const onSubmit = useCallback(
    async (data: IEventStepOne) => {
      const processedData = {
        ...data,
        extraCost: convertCurrencyToCents(data.extraCost),
        installmentConfig: {
          ...data.installmentConfig,
          minAmountForInstallment: convertCurrencyToCents(
            data.installmentConfig.minAmountForInstallment,
          ),
        },
      }
      handleNext(processedData)
    },
    [handleNext, convertCurrencyToCents],
  )

  return (
    <>
      <Stack gap={4}>
        {/* Basic Information */}
        <Card>
          <CardContent>
            <Stack gap={3}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify icon='mdi:calendar-star' />
                <Typography variant='h6'>Informações Básicas</Typography>
              </Stack>

              <Box
                rowGap={3}
                columnGap={2}
                display='grid'
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                }}
              >
                <RHFTextField
                  name='name'
                  label='Nome do Evento'
                  placeholder='Ex: Show de Rock, Festa Junina...'
                />
                <Controller
                  name='date'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label='Data do evento'
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue)
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
                <RHFTextField
                  name='local'
                  label='Local do evento'
                  placeholder='Nome do local/estabelecimento'
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Tax Configuration */}
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify icon='mdi:percent' />
                <Typography variant='h6'>Taxas por forma de pagamento</Typography>
              </Stack>

              <Box
                rowGap={2}
                columnGap={2}
                display='grid'
                gridTemplateColumns={{
                  xs: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                }}
              >
                <PaymentTaxField name='money' label='Dinheiro' icon='mdi:cash' color='success' />
                <PaymentTaxField name='debit' label='Débito' icon='mdi:credit-card' color='info' />
                <PaymentTaxField name='pix' label='PIX' icon='mdi:qrcode' color='warning' />
                <PaymentTaxField
                  name='credit'
                  label='Crédito'
                  icon='mdi:credit-card-outline'
                  color='error'
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Installment Configuration */}
        <Card>
          <CardContent>
            <Stack gap={3}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify icon='mdi:credit-card-multiple' />
                <Typography variant='h6'>Configuração de Parcelamento</Typography>
              </Stack>

              <Typography variant='body2' color='text.secondary'>
                Configure o parcelamento para pagamentos com cartão de crédito
              </Typography>

              <Controller
                name='installmentConfig.enabled'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                        color='primary'
                      />
                    }
                    label='Habilitar parcelamento'
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 600,
                      },
                    }}
                  />
                )}
              />

              {watch('installmentConfig.enabled') && (
                <Box
                  rowGap={3}
                  columnGap={2}
                  display='grid'
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                >
                  <RHFTextField
                    name='installmentConfig.maxInstallments'
                    label='Máximo de parcelas'
                    type='number'
                    placeholder='12'
                    onChange={handleMaxInstallmentsChange}
                    helperText='Número máximo de parcelas (1-24)'
                  />
                  <RHFTextField
                    name='installmentConfig.minAmountForInstallment'
                    label='Valor mínimo para parcelar (R$)'
                    mask='currency'
                    placeholder='0,00'
                    helperText='Valor mínimo para permitir parcelamento'
                  />
                </Box>
              )}

              {watch('installmentConfig.enabled') && (
                <>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'info.lighter',
                      border: '1px solid',
                      borderColor: 'info.main',
                    }}
                  >
                    <Stack direction='row' gap={1} alignItems='flex-start'>
                      <Iconify icon='mdi:information' sx={{ color: 'info.main', mt: 0.2 }} />
                      <Box>
                        <Typography variant='subtitle2' color='info.dark'>
                          Importante sobre parcelamento
                        </Typography>
                        <Typography variant='body2' color='info.dark'>
                          • O parcelamento está disponível apenas para cartão de crédito
                          <br />
                          • Cartão de débito não possui opção de parcelamento
                          <br />• PIX e dinheiro são sempre à vista
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {eventId && (
                    <Stack direction='row' justifyContent='flex-end'>
                      <LoadingButton
                        variant='contained'
                        color='primary'
                        startIcon={<Iconify icon='mdi:content-save' />}
                        loading={isLoadingInstallment}
                        onClick={handleSaveInstallmentConfig}
                        size='medium'
                      >
                        Salvar Configurações de Parcelamento
                      </LoadingButton>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Additional Settings */}
        <Card>
          <CardContent>
            <Stack gap={3}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify icon='mdi:cog' />
                <Typography variant='h6'>Configurações Adicionais</Typography>
              </Stack>

              <Box
                rowGap={3}
                columnGap={2}
                display='grid'
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                }}
              >
                <RHFTextField
                  name='superPassword'
                  label='Senha administrativa'
                  type='password'
                  placeholder='Senha para funções administrativas'
                />
                <RHFTextField
                  name='extraCost'
                  label='Custos extras (R$)'
                  placeholder='0,00'
                  mask='currency'
                />
              </Box>

              {/*    <RHFTextField
                name='obversation'
                minRows={3}
                multiline
                label='Observações'
                placeholder='Informações adicionais sobre o evento...'
              /> */}
            </Stack>
          </CardContent>
        </Card>

        {/* Webhook Configuration */}
        <Card>
          <CardContent>
            <Stack gap={3}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify icon='mdi:webhook' />
                <Typography variant='h6'>Configuração de Webhook</Typography>
              </Stack>

              <Typography variant='body2' color='text.secondary'>
                Configure uma URL para receber notificações automáticas quando um pedido for criado neste evento
              </Typography>

              <Controller
                name='webhook.enabled'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                        color='primary'
                      />
                    }
                    label='Habilitar webhook'
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 600,
                      },
                    }}
                  />
                )}
              />

              {watch('webhook.enabled') && (
                <Fade in={watch('webhook.enabled')}>
                  <Stack gap={2}>
                    <RHFTextField
                      name='webhook.url'
                      label='URL do Webhook'
                      placeholder='https://seu-dominio.com/webhook'
                      helperText='A URL deve aceitar requisições POST com dados do pedido em JSON'
                    />

                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: 'info.lighter',
                        border: '1px solid',
                        borderColor: 'info.main',
                      }}
                    >
                      <Stack gap={1}>
                        <Stack direction='row' gap={1} alignItems='center'>
                          <Iconify icon='mdi:information' sx={{ color: 'info.main' }} />
                          <Typography variant='subtitle2' color='info.dark'>
                            Como funciona o Webhook?
                          </Typography>
                        </Stack>
                        <Typography variant='body2' color='info.dark'>
                          • Quando um pedido for criado, enviaremos automaticamente os dados via POST
                          <br />
                          • Os dados incluem informações do pedido, produtos e evento
                          <br />
                          • O formato é JSON com campo &quot;event&quot;: &quot;order.created&quot;
                          <br />• Timeout de 5 segundos - não bloqueia a criação do pedido
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Fade>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* TEF Configuration (DevBrasil machines only) */}
        <Card>
          <CardContent>
            <Stack gap={3}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify icon='mdi:credit-card-settings-outline' />
                <Typography variant='h6'>Configuração TEF (Máquinas DevBrasil)</Typography>
              </Stack>

              <Typography variant='body2' color='text.secondary'>
                Estes dados são usados apenas nas máquinas da DevBrasil (SiTef). Quando ativado, os
                campos abaixo serão enviados para a máquina e pré-preencherão o diálogo de{' '}
                <strong>Ativar TEF</strong>. Campos deixados em branco manterão o que já estiver
                salvo localmente na máquina. Campos individuais de cada máquina (Terminal ID,
                Token, Business ID, ID Estabelecimento, E-mail e Servidor) continuam configurados
                apenas na própria máquina.
              </Typography>

              <Controller
                name='tefConfig.enabled'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                        color='primary'
                      />
                    }
                    label='Evento utiliza máquinas DevBrasil (TEF SiTef)'
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 600,
                      },
                    }}
                  />
                )}
              />

              {watch('tefConfig.enabled') && (
                <Fade in={watch('tefConfig.enabled')}>
                  <Stack gap={2}>
                    <Box
                      rowGap={3}
                      columnGap={2}
                      display='grid'
                      gridTemplateColumns={{
                        xs: 'repeat(1, 1fr)',
                        sm: 'repeat(2, 1fr)',
                      }}
                    >
                      <RHFTextField
                        name='tefConfig.clientCode'
                        label='Código Cliente (CNPJ do Estabelecimento)'
                        placeholder='Ex: 12345678000199'
                      />

                      <RHFTextField
                        name='tefConfig.softDescriptor'
                        label='Soft Descriptor'
                        placeholder='Ex: AMP*Nome da Loja'
                        inputProps={{ maxLength: 22 }}
                        helperText='Máx. 22 caracteres'
                      />

                      <RHFTextField
                        name='tefConfig.nomeCompleto'
                        label='Razão Social / Nome Completo'
                        placeholder='Ex: TESTEPAG SOLUCOES DE PAGAMENTO LTDA'
                        inputProps={{ maxLength: 60 }}
                      />

                      <RHFTextField
                        select
                        SelectProps={{ native: true }}
                        name='tefConfig.tipoPessoa'
                        label='Tipo de Pessoa'
                      >
                        <option value='J'>Jurídica (J)</option>
                        <option value='F'>Física (F)</option>
                      </RHFTextField>

                      <RHFTextField
                        name='tefConfig.cnpj'
                        label='CPF ou CNPJ (somente dígitos)'
                        placeholder='Ex: 07905082000162'
                        inputProps={{ maxLength: 14 }}
                        helperText='CPF: 11 dígitos | CNPJ: 14 dígitos'
                      />

                      <RHFTextField
                        name='tefConfig.telefone'
                        label='Telefone (DDD + número)'
                        placeholder='Ex: 11951949080'
                        inputProps={{ maxLength: 11 }}
                      />

                      <RHFTextField
                        name='tefConfig.endereco'
                        label='Endereço'
                        placeholder='Ex: RUA JOSE BONIFACIO'
                        inputProps={{ maxLength: 48 }}
                      />

                      <RHFTextField
                        name='tefConfig.cidade'
                        label='Cidade'
                        placeholder='Ex: SOCORRO'
                        inputProps={{ maxLength: 13 }}
                        helperText='Máx. 13 caracteres'
                      />

                      <RHFTextField
                        name='tefConfig.uf'
                        label='UF'
                        placeholder='Ex: SP'
                        inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                      />

                      <RHFTextField
                        name='tefConfig.pais'
                        label='País (3 letras)'
                        placeholder='BRA'
                        inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                      />

                      <RHFTextField
                        name='tefConfig.cep'
                        label='CEP (sem hífen)'
                        placeholder='Ex: 12960000'
                        inputProps={{ maxLength: 8 }}
                      />

                      <RHFTextField
                        name='tefConfig.mcc'
                        label='MCC (Merchant Category Code)'
                        placeholder='Ex: 1999'
                        inputProps={{ maxLength: 4 }}
                      />
                    </Box>

                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: 'warning.lighter',
                        border: '1px solid',
                        borderColor: 'warning.main',
                      }}
                    >
                      <Stack direction='row' gap={1} alignItems='flex-start'>
                        <Iconify
                          icon='mdi:information'
                          sx={{ color: 'warning.dark', mt: 0.2 }}
                        />
                        <Box>
                          <Typography variant='subtitle2' color='warning.dark'>
                            Como funciona
                          </Typography>
                          <Typography variant='body2' color='warning.dark'>
                            • Ao abrir o evento na máquina e clicar em <strong>Ativar TEF</strong>,
                            os campos acima serão pré-preenchidos automaticamente.
                            <br />• Se algum campo aqui ficar em branco, a máquina usará o valor que
                            já estava salvo localmente.
                            <br />• Os campos específicos da máquina (Terminal ID, Token Registro,
                            etc.) não são alterados.
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Stack>
                </Fade>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Stack alignItems='flex-end' justifyContent='space-between' direction='row' sx={{ mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          variant='outlined'
          onClick={handleBack}
          startIcon={<Iconify icon='solar:arrow-left-bold' />}
        >
          Voltar
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit(onSubmit)}
          size='large'
          endIcon={<Iconify icon='solar:arrow-right-bold' />}
          sx={{
            minWidth: 200,
            fontWeight: 600,
          }}
        >
          Salvar e Continuar
        </Button>
      </Stack>
    </>
  )
}

// ----------------------------------------------------------------------

interface PaymentTaxFieldProps {
  name: string
  label: string
  icon: string
  color: 'success' | 'info' | 'warning' | 'error'
}

const PaymentTaxField = ({ name, label, icon, color }: PaymentTaxFieldProps) => (
  <RHFTextField
    size='small'
    mask='decimal'
    name={`tax.${name}`}
    label={label}
    placeholder='0,00'
    InputProps={{
      startAdornment: (
        <Iconify icon={icon} sx={{ color: `${color}.main`, mr: 1, flexShrink: 0 }} />
      ),
      endAdornment: (
        <Typography variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
          %
        </Typography>
      ),
    }}
    sx={{
      '& .MuiInputBase-input': {
        textAlign: 'right',
        fontWeight: 'bold',
      },
    }}
  />
)
